import client from './RestClient';
import { L1ObjectObjective } from 'core/l1Object/L1Object';
import _ from 'lodash';
import { AxiosInstance } from 'axios';
import { RtbObjective } from 'core/rtbCampaignGroup/RtbCampaignGroup';
import { ChannelGroupSetting } from 'core/channelGroup/ChannelGroupSettings';

export type L1ObjectUpdateRequest = {
  name: string;
  l1ObjectLifetimeBudget?: number;
  autoOptimise: boolean;
  channelConfig?: any;
};

export type L1ObjectCreateRequest = L1ObjectUpdateRequest & {
  adsOrderId: number;
  channel: string;
  channelGroupId: number;
};

export interface L1ObjectWebService {
  createL1Object(l1Object: L1ObjectCreateRequest): Promise<number>;
  updateL1Object(
    l1ObjectId: number | string,
    l1Object: L1ObjectUpdateRequest
  ): Promise<void>;
  getL1Object(l1ObjectId: number | string): Promise<any>;
  getL1Objects(orderId: number | string): Promise<Array<any>>;
  deleteL1Object(l1ObjectId: number): Promise<void>;
  updateL1ObjectState(
    l1ObjectIds: (number | string)[],
    state: 'activate' | 'deactivate'
  ): Promise<void>;
  getL1ObjectOptions(): Promise<SelectOptions[]>;
  getOrderNumber(l1ObjectId: string | number): Promise<string>;
}

const wrapL1Object = (json) => {
  const objectiveMap = {
    [RtbObjective.REACH]: L1ObjectObjective.AWARENESS,
    [RtbObjective.LINK_CLICKS]: L1ObjectObjective.TRAFFIC,
    [RtbObjective.AWARENESS]: L1ObjectObjective.AWARENESS,
    [RtbObjective.TRAFFIC]: L1ObjectObjective.TRAFFIC,
    [RtbObjective.SALES]: L1ObjectObjective.SALES
  };
  const transToPMAX3Objective = (objective) =>
    objectiveMap[objective] ? objectiveMap[objective] : objective;
  return {
    ...json,
    budget: json.l1ObjectLifetimeBudget,
    dailyBudget: json.l1ObjectDailyBudget,
    objective: transToPMAX3Objective(json.objective),
    l2ObjectMinStartDate: _.get(json, 'l2ObjectMinStartDate'),
    l2ObjectMaxEndDate: _.get(json, 'l2ObjectMaxEndDate'),
    l2ObjectRunningStatus: _.get(json, 'l2ObjectRunningStatus'),
    actionable: _.get(json, 'actionable'),
    channel: json.channelGroupId
      ? ChannelGroupSetting.fromId(json.channelGroupId).key
      : json.channel
  };
};

export class RestfulL1ObjectWebService implements L1ObjectWebService {
  restClient: AxiosInstance;

  constructor(restClient: AxiosInstance = client) {
    this.restClient = restClient;
  }

  async createL1Object(l1Object: L1ObjectCreateRequest): Promise<number> {
    const response = await this.restClient.post('/v3/l1-objects', l1Object);
    return response.data.l1ObjectId;
  }
  async updateL1Object(
    l1ObjectId: number | string,
    l1Object: L1ObjectUpdateRequest
  ): Promise<void> {
    return this.restClient.put(`/v3/l1-objects/${l1ObjectId}`, l1Object);
  }
  async getL1Object(l1ObjectId: number | string): Promise<any> {
    const response = await this.restClient.get(`/v3/l1-objects/${l1ObjectId}`);
    return wrapL1Object(response.data);
  }
  async getL1Objects(orderId: number | string): Promise<any> {
    const response = await this.restClient.get(
      `/v3/adsOrders/${orderId}/l1-objects`
    );
    return _.get(response.data, 'records', []).map(wrapL1Object);
  }
  async deleteL1Object(l1ObjectId: number): Promise<void> {
    await this.restClient.delete(`/v3/l1-objects/${l1ObjectId}`);
  }
  async updateL1ObjectState(
    l1ObjectIds: (number | string)[],
    state: 'activate' | 'deactivate'
  ): Promise<void> {
    const l1ObjectParameters = l1ObjectIds.join(',');
    await this.restClient.put(
      `/v3/l1-objects/state/${state}?l1ObjectIds=${l1ObjectParameters}`
    );
  }
  async getL1ObjectOptions(): Promise<SelectOptions[]> {
    const response = await this.restClient.get(`/v3/l1-objects/options`);
    return response.data.records;
  }
  async getOrderNumber(l1ObjectId: string | number): Promise<string> {
    const response = await this.restClient.get(
      `/v2/l1-objects/${l1ObjectId}/orderNumber`
    );
    return response.data;
  }
}
